import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  PERMISSIONS_CONFIG,
  getActionsGroupedByPage,
  FULFILLMENT_ACTIONS,
  FULFILLMENT_PRESETS,
} from '../permissions';
import Modal from '../components/Modal';
import OrderDeskAccessModal from '../components/orderdesk/OrderDeskAccessModal';
import './UserManagementPage.css';

const FULFILLMENT_STATION_GROUPS = [
  {
    station: 'General Hub Access',
    description: 'Access the Fulfillment Hub overview, order queues, and metrics',
    actions: ['fulfillment.view'],
  },
  {
    station: 'Station 1: Order Intake & Verification',
    description: 'Verify COD orders and confirm custom/B2B orders',
    actions: ['fulfillment.verify'],
  },
  {
    station: 'Station 2: Warehouse Picking & "Mark Used" Desk',
    description: 'Consolidated shelving pick lists and instant inventory locking',
    actions: ['fulfillment.pick', 'fulfillment.markUsed'],
  },
  {
    station: 'Station 3: Production Batches & Inbound QC',
    description: 'Consolidated deficit batch compilation, factory POs, and 1-by-1 QC receipt',
    actions: ['fulfillment.createBatch', 'fulfillment.receiveBatch'],
  },
  {
    station: 'Station 4: Packing Station & Courier Handover',
    description: 'Weight scale checks, carrier shipping labels, box packing, and driver manifest',
    actions: ['fulfillment.generateLabel', 'fulfillment.pack', 'fulfillment.dispatch'],
  },
  {
    station: 'Station 6: Live Tracking & NDR Action Center',
    description: 'Real-time courier delivery issues, 1-click WhatsApp customer rescue, and reattempts',
    actions: ['fulfillment.handleNDR'],
  },
  {
    station: 'Station 7: Returns, Restock & Exchanges',
    description: 'Inbound return QC grading, auto Zoho Credit Notes, and 1-click replacement orders',
    actions: ['fulfillment.processReturns', 'fulfillment.createExchange'],
  },
];

function UserManagementPage() {
  const [deskUser, setDeskUser] = useState(null); // Order Desk access window
  const [switchingUid, setSwitchingUid] = useState(null);
  const { currentUser, isAdmin, startViewAs } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [permissions, setPermissions] = useState({ pages: [], actions: [] });

  // Dedicated Fulfillment Access state
  const [selectedFulfillmentUser, setSelectedFulfillmentUser] = useState(null);
  const [fulfillmentPerms, setFulfillmentPerms] = useState({ hasPageAccess: false, actions: [] });

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createEmail, setCreateEmail] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null); // { email, password }

  const fetchUsers = useCallback(async () => {
    if (!currentUser) return;
    setIsLoading(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/admin/users', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to fetch users.');
      }
      const data = await response.json();
      const list = Array.isArray(data?.users) ? data.users : [];
      list.sort((a, b) =>
        String(a?.email || '').localeCompare(String(b?.email || ''), undefined, { sensitivity: 'base' })
      );
      setUsers(list);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleViewAs = async (user) => {
    setSwitchingUid(user.uid);
    setError('');
    try {
      await startViewAs(user.uid);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Could not start View as.');
    } finally {
      setSwitchingUid(null);
    }
  };

  const handleOpenEditModal = (user) => {
    const rawPages = user.customClaims?.permissions?.pages;
    const rawActions = user.customClaims?.permissions?.actions;
    setSelectedUser(user);
    setPermissions({
      pages: (Array.isArray(rawPages) ? rawPages : []).filter(Boolean).map(String),
      actions: (Array.isArray(rawActions) ? rawActions : []).filter(Boolean).map(String),
    });
  };

  const handleCloseEditModal = () => {
    setSelectedUser(null);
    setPermissions({ pages: [], actions: [] });
  };

  const handlePermissionChange = (type, id, checked) => {
    setPermissions(prev => {
      const current = new Set(prev[type]);
      if (checked) {
        current.add(id);
      } else {
        current.delete(id);
      }
      return { ...prev, [type]: Array.from(current) };
    });
  };

  const applyPreset = (presetKey) => {
    if (presetKey === 'tiff_operator') {
      setPermissions({
        pages: ['batches'],
        actions: ['batches.prepareProductionFiles'],
      });
    } else if (presetKey === 'production_manager') {
      setPermissions({
        pages: ['batches'],
        actions: [
          'batches.create',
          'batches.receive',
          'batches.undoReceive',
          'batches.exportCsv',
          'batches.markReceived',
          'batches.delete',
          'batches.prepareProductionFiles',
          'batches.viewCompleted',
          'batches.viewStockDetails',
          'batches.printPdf',
          'batches.viewPendingReport',
          'inventory.printLabels',
        ],
      });
    } else if (presetKey === 'clear') {
      setPermissions({ pages: [], actions: [] });
    }
  };

  // ── Dedicated Fulfillment Handlers ─────────────────────────────────────
  const handleOpenFulfillmentModal = (user) => {
    const rawPages = user.customClaims?.permissions?.pages || [];
    const rawActions = user.customClaims?.permissions?.actions || [];
    setSelectedFulfillmentUser(user);
    setFulfillmentPerms({
      hasPageAccess: rawPages.includes('fulfillment'),
      actions: (Array.isArray(rawActions) ? rawActions : []).filter((a) =>
        String(a).startsWith('fulfillment.')
      ),
    });
  };

  const handleCloseFulfillmentModal = () => {
    setSelectedFulfillmentUser(null);
    setFulfillmentPerms({ hasPageAccess: false, actions: [] });
  };

  const handleFulfillmentActionToggle = (actionId, checked) => {
    setFulfillmentPerms((prev) => {
      const current = new Set(prev.actions);
      if (checked) {
        current.add(actionId);
      } else {
        current.delete(actionId);
      }
      const newActions = Array.from(current);
      return {
        ...prev,
        actions: newActions,
        // Auto-grant hub page access if user has any fulfillment actions
        hasPageAccess: newActions.length > 0 ? true : prev.hasPageAccess,
      };
    });
  };

  const handleFulfillmentPageToggle = (checked) => {
    setFulfillmentPerms((prev) => ({
      ...prev,
      hasPageAccess: checked,
    }));
  };

  const applyFulfillmentPreset = (presetKey) => {
    if (presetKey === 'clear') {
      setFulfillmentPerms({ hasPageAccess: false, actions: [] });
      return;
    }
    const preset = FULFILLMENT_PRESETS[presetKey];
    if (preset) {
      setFulfillmentPerms({
        hasPageAccess: true,
        actions: [...preset.actions],
      });
    }
  };

  const handleSaveFulfillmentChanges = async () => {
    if (!selectedFulfillmentUser) return;
    setIsEditing(true);
    setError('');
    try {
      const rawPages = selectedFulfillmentUser.customClaims?.permissions?.pages || [];
      const rawActions = selectedFulfillmentUser.customClaims?.permissions?.actions || [];

      // Preserve non-fulfillment pages & actions
      const nonFulfillmentPages = (Array.isArray(rawPages) ? rawPages : []).filter(
        (p) => p !== 'fulfillment'
      );
      const nonFulfillmentActions = (Array.isArray(rawActions) ? rawActions : []).filter(
        (a) => !String(a).startsWith('fulfillment.')
      );

      const mergedPages =
        fulfillmentPerms.hasPageAccess || fulfillmentPerms.actions.length > 0
          ? [...nonFulfillmentPages, 'fulfillment']
          : nonFulfillmentPages;
      const mergedActions = [...nonFulfillmentActions, ...fulfillmentPerms.actions];

      const token = await currentUser.getIdToken();
      const response = await fetch(
        `/api/v1/admin/users/${selectedFulfillmentUser.uid}/claims`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            claims: {
              permissions: {
                pages: mergedPages,
                actions: mergedActions,
              },
            },
          }),
        }
      );
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to save fulfillment permissions.');
      }
      await fetchUsers(); // Refresh the user list
      handleCloseFulfillmentModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsEditing(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!selectedUser) return;
    setIsEditing(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/v1/admin/users/${selectedUser.uid}/claims`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ claims: { permissions } }),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to save permissions.');
      }
      await fetchUsers(); // Refresh the user list
      handleCloseEditModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsEditing(false);
    }
  };

  const openCreateModal = () => {
    setError('');
    setCreatedCredentials(null);
    setCreateEmail('');
    setCreatePassword('');
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    if (isCreating) return;
    setIsCreateModalOpen(false);
    setCreateEmail('');
    setCreatePassword('');
  };

  const handleCreateUser = async () => {
    if (!currentUser) return;
    const email = String(createEmail || '').trim();
    const password = String(createPassword || '');
    if (!email || !password) {
      setError('Email and password are required to create a user.');
      return;
    }
    setIsCreating(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/admin/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create user.');
      }

      setCreatedCredentials({ email, password });
      await fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsCreating(false);
    }
  };

  const copyCreatedPassword = async () => {
    if (!createdCredentials?.password) return;
    try {
      await navigator.clipboard.writeText(createdCredentials.password);
      alert('Password copied to clipboard.');
    } catch (err) {
      alert('Could not copy. Please copy manually.');
    }
  };

  return (
    <div className="user-management-container">
      <div className="user-management-header">
        <h1>User Management</h1>
        <button className="action-button save-btn" onClick={openCreateModal}>
          Add User
        </button>
      </div>
      {error && <p className="error-message">{error}</p>}
      <div className="table-wrapper">
        {isLoading && <div className="loading-overlay"><p>Loading Users...</p></div>}
        <table className={`users-table ${isLoading ? 'loading' : ''}`}>
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Last Sign-In</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.uid}>
                <td>{user.email}</td>
                <td>{user.isAdmin ? <span className="role-tag admin">Admin</span> : <span className="role-tag user">User</span>}</td>
                <td>{user.lastSignInTime ? new Date(user.lastSignInTime).toLocaleString() : 'Never'}</td>
                <td>
                  <div className="user-actions-cell">
                    {isAdmin && !user.isAdmin && <button
                      type="button"
                      className="user-access-eye"
                      aria-label={`View app as ${user.email}`}
                      title={`View app as ${user.email}`}
                      disabled={!!switchingUid}
                      onClick={() => handleViewAs(user)}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M2.25 12s3.5-6 9.75-6 9.75 6 9.75 6-3.5 6-9.75 6-9.75-6-9.75-6Z"/><circle cx="12" cy="12" r="2.75"/></svg>
                    </button>}
                    {!user.isAdmin && <>
                      <button onClick={() => handleOpenEditModal(user)} className="action-button">
                        Edit Permissions
                      </button>
                      <button
                        onClick={() => handleOpenFulfillmentModal(user)}
                        className="action-button fulfillment-btn"
                        title="Configure Station & Fulfillment Access"
                      >
                        📦 Fulfillment Access
                      </button>
                      <button
                        onClick={() => setDeskUser(user)}
                        className="action-button fulfillment-btn"
                        title="Order Desk role, tabs and actions"
                      >
                        🗂 Order Desk Access
                      </button>
                    </>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isCreateModalOpen && (
        <Modal isOpen={isCreateModalOpen} onClose={closeCreateModal} contentClassName="user-create-modal-content">
          <h2>Add User</h2>
          <p className="user-create-help">Creates a new Firebase Auth user (email + password). Then you can assign permissions.</p>

          <div className="user-create-form">
            <label>
              Email
              <input
                type="email"
                value={createEmail}
                onChange={(e) => setCreateEmail(e.target.value)}
                placeholder="user@example.com"
                disabled={isCreating}
              />
            </label>
            <label>
              Temporary Password
              <input
                type="text"
                value={createPassword}
                onChange={(e) => setCreatePassword(e.target.value)}
                placeholder="Enter a password to share"
                disabled={isCreating}
              />
            </label>
          </div>

          {createdCredentials && (
            <div className="user-create-success">
              <div><strong>User created:</strong> {createdCredentials.email}</div>
              <div className="user-create-password-row">
                <span><strong>Password:</strong> <code>{createdCredentials.password}</code></span>
                <button type="button" className="action-button" onClick={copyCreatedPassword}>
                  Copy
                </button>
              </div>
              <div className="user-create-next">Next: select the user in the list and click “Edit Permissions” or “📦 Fulfillment Access”.</div>
            </div>
          )}

          <div className="modal-actions">
            <button onClick={closeCreateModal} className="action-button cancel-btn" disabled={isCreating}>
              Close
            </button>
            <button onClick={handleCreateUser} disabled={isCreating} className="action-button save-btn">
              {isCreating ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </Modal>
      )}

      {/* General Permissions Modal */}
      {selectedUser && (
        <Modal isOpen={!!selectedUser} onClose={handleCloseEditModal} contentClassName="permissions-modal-content">
          <h2>Edit Permissions for {selectedUser.email}</h2>

          <div className="permissions-presets-bar">
            <span className="presets-label">⚡ 1-Click Presets:</span>
            <button
              type="button"
              className="preset-btn preset-btn--operator"
              onClick={() => applyPreset('tiff_operator')}
              title="Only allows preparing TIFFs on pending batches; hides stock/cost/allocations and completed batches"
            >
              Production TIFF Operator (Restricted)
            </button>
            <button
              type="button"
              className="preset-btn preset-btn--manager"
              onClick={() => applyPreset('production_manager')}
              title="Full access to all production batch actions, columns, and history"
            >
              Production Manager (Full Access)
            </button>
            <button
              type="button"
              className="preset-btn preset-btn--clear"
              onClick={() => applyPreset('clear')}
            >
              Clear All
            </button>
          </div>

          <div className="permissions-grid">
            <div className="permission-group">
              <h3>Page Access</h3>
              {PERMISSIONS_CONFIG.pages.map(page => (
                <label key={page.id} className="permission-checkbox">
                  <input
                    type="checkbox"
                    checked={permissions.pages.includes(page.id)}
                    onChange={(e) => handlePermissionChange('pages', page.id, e.target.checked)}
                  />
                  {page.name}
                </label>
              ))}
            </div>
            <div className="permission-group permission-group--actions">
              <h3>Action Access</h3>
              {getActionsGroupedByPage().map(group => (
                <div key={group.pageId} className="permission-action-group">
                  <div className="permission-action-group-header">{group.pageName}</div>
                  {group.actions.map(action => (
                    <label key={action.id} className="permission-checkbox permission-checkbox--action">
                      <input
                        type="checkbox"
                        checked={permissions.actions.includes(action.id)}
                        onChange={(e) => handlePermissionChange('actions', action.id, e.target.checked)}
                      />
                      {action.name}
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="modal-actions">
            <button onClick={handleCloseEditModal} className="action-button cancel-btn">Cancel</button>
            <button onClick={handleSaveChanges} disabled={isEditing} className="action-button save-btn">
              {isEditing ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {deskUser && (
        <OrderDeskAccessModal
          user={deskUser}
          getToken={() => currentUser.getIdToken()}
          onClose={() => setDeskUser(null)}
          onSaved={fetchUsers}
        />
      )}

      {/* Dedicated Fulfillment Station Access Modal */}
      {selectedFulfillmentUser && (
        <Modal
          isOpen={!!selectedFulfillmentUser}
          onClose={handleCloseFulfillmentModal}
          contentClassName="permissions-modal-content fulfillment-modal-content"
        >
          <div className="fulfillment-modal-header">
            <h2>📦 Fulfillment Station Access: {selectedFulfillmentUser.email}</h2>
            <p className="fulfillment-modal-subtitle">
              Configure granular action access across Warehouse Picking, Production Batching, Shipping Labels, Invoicing, NDR, and Returns.
            </p>
          </div>

          <div className="permissions-presets-bar fulfillment-presets-bar">
            <span className="presets-label">⚡ Station 1-Click Roles:</span>
            <button
              type="button"
              className="preset-btn fulfillment-preset-btn picker"
              onClick={() => applyFulfillmentPreset('picker')}
              title={FULFILLMENT_PRESETS.picker.description}
            >
              {FULFILLMENT_PRESETS.picker.label}
            </button>
            <button
              type="button"
              className="preset-btn fulfillment-preset-btn packer"
              onClick={() => applyFulfillmentPreset('packer')}
              title={FULFILLMENT_PRESETS.packer.description}
            >
              {FULFILLMENT_PRESETS.packer.label}
            </button>
            <button
              type="button"
              className="preset-btn fulfillment-preset-btn production"
              onClick={() => applyFulfillmentPreset('production')}
              title={FULFILLMENT_PRESETS.production.description}
            >
              {FULFILLMENT_PRESETS.production.label}
            </button>
            <button
              type="button"
              className="preset-btn fulfillment-preset-btn returns"
              onClick={() => applyFulfillmentPreset('returns_ndr')}
              title={FULFILLMENT_PRESETS.returns_ndr.description}
            >
              {FULFILLMENT_PRESETS.returns_ndr.label}
            </button>
            <button
              type="button"
              className="preset-btn fulfillment-preset-btn admin-lead"
              onClick={() => applyFulfillmentPreset('admin')}
              title={FULFILLMENT_PRESETS.admin.description}
            >
              {FULFILLMENT_PRESETS.admin.label}
            </button>
            <button
              type="button"
              className="preset-btn preset-btn--clear"
              onClick={() => applyFulfillmentPreset('clear')}
            >
              Clear Station Access
            </button>
          </div>

          <div className="fulfillment-page-toggle-row">
            <label className="fulfillment-page-checkbox">
              <input
                type="checkbox"
                checked={fulfillmentPerms.hasPageAccess}
                onChange={(e) => handleFulfillmentPageToggle(e.target.checked)}
              />
              <strong>Allow Main Fulfillment Hub Page Access (`/fulfillment`)</strong>
            </label>
            <span className="fulfillment-badge-counter">
              {fulfillmentPerms.actions.length} / {FULFILLMENT_ACTIONS.length} Actions Active
            </span>
          </div>

          <div className="fulfillment-stations-grid">
            {FULFILLMENT_STATION_GROUPS.map((group) => {
              const groupActions = FULFILLMENT_ACTIONS.filter((a) =>
                group.actions.includes(a.id)
              );
              return (
                <div key={group.station} className="fulfillment-station-card">
                  <div className="fulfillment-station-card-header">
                    <h4>{group.station}</h4>
                    <p>{group.description}</p>
                  </div>
                  <div className="fulfillment-station-card-body">
                    {groupActions.map((action) => (
                      <label key={action.id} className="permission-checkbox fulfillment-checkbox">
                        <input
                          type="checkbox"
                          checked={fulfillmentPerms.actions.includes(action.id)}
                          onChange={(e) =>
                            handleFulfillmentActionToggle(action.id, e.target.checked)
                          }
                        />
                        <span className="fulfillment-action-name">{action.name}</span>
                        <code className="fulfillment-action-id">{action.id}</code>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="modal-actions">
            <button
              onClick={handleCloseFulfillmentModal}
              className="action-button cancel-btn"
              disabled={isEditing}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveFulfillmentChanges}
              disabled={isEditing}
              className="action-button save-btn"
            >
              {isEditing ? 'Saving...' : 'Save Fulfillment Access'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default UserManagementPage;
